import React, { useEffect, useState } from 'react';
import { Box, Typography, Alert, Snackbar } from '@mui/material';
import { Users, UserPlus } from 'lucide-react';
import { ClientesTable } from '../components/ClientesTable';
import { ClienteFiltros as FiltrosBar } from '../components/ClienteFiltros';
import { NuevoClienteDrawer } from '../components/NuevoClienteDrawer';
import InfoCards from '../components/InfoCards';
import EstadoFilter from '../components/EstadoFilter';
import { useClientes, EstadoFilter as EstadoFilterType } from '../hooks/useClientes';
import { clienteService } from '../services/clienteService';
import { ClienteDetalle, ClienteListItem } from '../types';

export function ClientesPage() {
  const {
    clientes,
    total,
    loading,
    error,
    filtros,
    estadoFilter,
    setEstadoFilter,
    metrics,
    aplicarFiltros,
    cambiarPagina,
    buscar,
    cancelBusqueda,
    recargar,
    eliminar,
    cargar,
  } = useClientes();

  const [nuevoDrawerOpen, setNuevoDrawerOpen] = useState(false);
  const [editarDrawerOpen, setEditarDrawerOpen] = useState(false);
  const [editarCliente, setEditarCliente] = useState<ClienteDetalle | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  useEffect(() => {
    cargar({ pagina: 1, limite: 10 });
  }, []);

  const handleAbrirEditar = async (cliente: ClienteListItem) => {
    const detalleCargado = await clienteService.obtener(cliente.id);
    setEditarCliente(detalleCargado);
    setEditarDrawerOpen(true);
  };

  const handleEliminar = async (cliente: ClienteListItem) => {
    if (!window.confirm(`¿Eliminar a "${cliente.razonSocial}"?`)) return;
    try {
      await eliminar(cliente.id);
      setSuccessMsg('Cliente eliminado correctamente');
    } catch {
      // error shown via useClientes
    }
  };

  const handleLimpiarFiltros = () => {
    cancelBusqueda();
    aplicarFiltros({ busqueda: undefined, activo: undefined });
  };

  const busquedaActiva = (filtros.busqueda?.length ?? 0) > 0;

  return (
    <Box
      data-testid="clientes-page"
      sx={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        background: '#F6F4F6',
        pt: { xs: '18px', md: '28px' },
        px: { xs: '14px', md: '32px' },
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Box
          sx={{
            width: 42,
            height: 42,
            background: '#F1E3ED',
            border: '1px solid #E5D2DF',
            borderRadius: '8px',
            boxShadow: '0 4px 10px rgba(116,48,95,0.10)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <Users size={25} color="#74305F" />
        </Box>

        <Box>
          <Typography sx={{ fontSize: 28, fontWeight: 750, color: '#241B25', lineHeight: 1.1 }}>
            Clientes
          </Typography>
          <Typography sx={{ fontSize: 14, color: '#756D77', lineHeight: 1.4 }}>
            Administra la informacion comercial y los contactos de tus clientes.
          </Typography>
        </Box>

        <Box sx={{ flex: 1 }} />

        <Box
          component="button"
          aria-label="Nuevo cliente"
          data-testid="nuevo-cliente-btn"
          onClick={() => setNuevoDrawerOpen(true)}
          sx={{
            height: 44,
            px: 2.5,
            borderRadius: '8px',
            border: 'none',
            background: '#A84F8D',
            color: '#FFFFFF',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            cursor: 'pointer',
            fontSize: 14,
            fontWeight: 600,
            boxShadow: '0 7px 16px rgba(126,48,103,0.23)',
            transition: 'background 150ms',
            '&:hover': { background: '#913E78' },
          }}
        >
          <UserPlus size={19} />
          Nuevo cliente
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <InfoCards
        total={metrics.total}
        activos={metrics.activos}
        nuevosEsteMes={metrics.nuevosEsteMes}
        loading={loading}
      />

      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 2, flexWrap: 'wrap' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <FiltrosBar
            filtros={filtros}
            onBuscar={buscar}
            onFiltrar={aplicarFiltros}
            onLimpiar={handleLimpiarFiltros}
          />
        </Box>
        <EstadoFilter
          value={estadoFilter}
          onChange={(val) => setEstadoFilter(val as EstadoFilterType)}
          loading={loading}
        />
      </Box>

      {/* Result summary */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', m: '18px 2px 10px' }}>
        <Typography sx={{ fontSize: 13, fontWeight: 600, color: '#6F6672' }}>
          {total} clientes encontrados
        </Typography>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0 }}>
        <ClientesTable
          clientes={clientes}
          total={total}
          pagina={filtros.pagina ?? 1}
          limite={filtros.limite ?? 10}
          loading={loading}
          error={error}
          busquedaActiva={busquedaActiva}
          onEditar={handleAbrirEditar}
          onEliminar={handleEliminar}
          onCambiarPagina={cambiarPagina}
          onCambiarLimite={(limite) => aplicarFiltros({ limite })}
          onRecargar={recargar}
          onCrearCliente={() => setNuevoDrawerOpen(true)}
        />
      </Box>

      <NuevoClienteDrawer
        open={nuevoDrawerOpen}
        onClose={() => setNuevoDrawerOpen(false)}
        onCreado={async () => {
          setSuccessMsg('Cliente creado correctamente');
          await recargar();
        }}
      />

      <NuevoClienteDrawer
        open={editarDrawerOpen}
        clienteEditar={editarCliente}
        onClose={() => { setEditarDrawerOpen(false); setEditarCliente(null); }}
        onActualizado={async () => {
          setSuccessMsg('Cliente actualizado correctamente');
          await recargar();
        }}
      />

      <Snackbar
        open={!!successMsg}
        autoHideDuration={3000}
        onClose={() => setSuccessMsg(null)}
        message={successMsg}
      />
    </Box>
  );
}
